import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Plus, Trash2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Vide } from "@/components/commun";
import { confirmer } from "@/lib/confirmer";

const lienInterface = (t) => `${window.location.origin}/partage/${t}`;
const lienTexte = (t) => `${process.env.REACT_APP_BACKEND_URL}/api/partage/public/${t}/texte`;

const copier = (texte) => navigator.clipboard.writeText(texte).then(() => toast.success("Lien copié.")).catch(() => toast.error("Copie impossible, sélectionnez le lien à la main."));

const LigneLien = ({ label, url, testId }) => (
  <div className="flex items-center gap-2 min-w-0">
    <div className="min-w-0 flex-1">
      <p className="text-xs at-muted font-semibold">{label}</p>
      <p className="text-sm truncate" data-testid={`${testId}-url`}>{url}</p>
    </div>
    <button type="button" onClick={() => copier(url)} className="at-btn shrink-0" aria-label={`Copier ${label}`} data-testid={`${testId}-copy`}><Copy size={16} /></button>
  </div>
);

const CarteLien = ({ l, revoquer }) => (
  <div className="at-fiche p-4 space-y-3" data-testid={`share-link-${l.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-bold truncate" data-testid={`share-link-name-${l.id}`}>{l.nom}</p>
        <p className="text-xs at-muted">Créé le {formatDateHeure(l.created_at)} · {l.lectures} lecture{l.lectures > 1 ? "s" : ""}{l.derniere_lecture ? ` · dernière le ${formatDateHeure(l.derniere_lecture)}` : ""}</p>
      </div>
      <button type="button" onClick={() => revoquer(l)} className="at-btn shrink-0 self-start" style={{ color: "var(--danger)" }} data-testid={`share-link-revoke-${l.id}`}><Trash2 size={16} />Révoquer</button>
    </div>
    <LigneLien label="Version texte pour Claude" url={lienTexte(l.token)} testId={`share-link-text-${l.id}`} />
    <LigneLien label="Interface en lecture seule" url={lienInterface(l.token)} testId={`share-link-ui-${l.id}`} />
  </div>
);

export default function Partage() {
  const [liste, setListe] = useState([]);
  const [nom, setNom] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const charger = useCallback(() => api.get("/partage").then((r) => setListe(r.data)).catch((e) => toast.error(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);

  const creer = async () => {
    setEnvoi(true);
    try { await api.post("/partage", { nom }); setNom(""); toast.success("Lien créé."); charger(); }
    catch (e) { toast.error(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const revoquer = async (l) => {
    if (!await confirmer(`Révoquer « ${l.nom} » ? Le lien cessera de fonctionner immédiatement.`)) return;
    try { await api.delete(`/partage/liens/${l.id}`); toast.success("Lien révoqué."); charger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };

  return (
    <div className="space-y-4" data-testid="share-settings">
      <div>
        <h2 className="text-base md:text-lg font-bold">Partage en lecture</h2>
        <p className="at-muted text-sm mt-1">Liens sans connexion montrant la structure et le fonctionnement du CRM (profils, processus, vues, connecteurs, chiffres globaux). Aucune fiche, aucun nom de client ni de partenaire. Valables jusqu'à révocation.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} placeholder="Nom du lien (ex. Lecture par Claude)" className="at-input flex-1" data-testid="share-link-name-input" />
        <button type="button" onClick={creer} disabled={envoi} className="at-btn at-btn-primary" data-testid="share-link-create-button"><Plus size={17} />{envoi ? "Création…" : "Créer un lien"}</button>
      </div>
      {liste.length === 0 && <Vide texte="Aucun lien de lecture actif." testId="share-links-empty" />}
      <div className="space-y-3" data-testid="share-links-list">
        {liste.map((l) => <CarteLien key={l.id} l={l} revoquer={revoquer} />)}
      </div>
      <p className="text-xs at-muted flex items-center gap-1"><Link2 size={14} />Pour Claude, donnez de préférence la version texte : elle se lit en entier en une seule ouverture.</p>
    </div>
  );
}
